import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase, SUPABASE_URL, SUPABASE_ANON_KEY } from '../../lib/supabase';
import { ArrowLeft, Shield, User, Phone, Mail, Lock, IdCard, Loader2, CheckCircle2, AlertCircle, Users } from 'lucide-react';
interface AdminForm {
  full_name: string;
  email: string;
  temporary_password: string;
  phone: string;
  national_id: string;
  role: string;
  is_active: boolean;
}
interface AdminResult {
  user_id?: string;
  email?: string;
  full_name?: string;
  role?: string;
  is_active?: boolean;
}
const ROLES = [{
  value: 'super_admin',
  label: 'Super Admin',
  desc: 'Acceso total'
}, {
  value: 'platform_admin',
  label: 'Admin de Plataforma',
  desc: 'Administración general'
}, {
  value: 'support',
  label: 'Soporte',
  desc: 'Acceso operativo'
}];
const inputClass = 'w-full bg-[#141414] border border-white/10 rounded-xl pl-12 pr-4 py-4 text-base text-white placeholder:text-white/15 focus:outline-none focus:border-[#006847] focus:ring-1 focus:ring-[#006847]';
const labelClass = 'block text-xs font-bold text-white/40 uppercase tracking-widest mb-2';
const iconClass = 'absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-white/20';
export function EnrollmentPlatformAdminPage() {
  const navigate = useNavigate();
  const [form, setForm] = useState<AdminForm>({
    full_name: '',
    email: '',
    temporary_password: '',
    phone: '',
    national_id: '',
    role: 'platform_admin',
    is_active: true
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<AdminResult | null>(null);
  const updateField = (field: keyof AdminForm, value: string | boolean) => {
    setForm(prev => ({
      ...prev,
      [field]: value
    }));
  };
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setResult(null);
    if (!form.full_name.trim()) return setError('El nombre es obligatorio.');
    if (!form.email.trim()) return setError('El correo es obligatorio.');
    if (!form.temporary_password) return setError('La contraseña es obligatoria.');
    if (form.temporary_password.length < 8) return setError('La contraseña debe tener al menos 8 caracteres.');
    if (!form.role) return setError('El rol es obligatorio.');
    setIsSubmitting(true);
    const payload = {
      fullName: form.full_name.trim(),
      email: form.email.trim().toLowerCase(),
      password: form.temporary_password,
      phone: form.phone.trim() || null,
      national_id: form.national_id.trim() || null,
      role: form.role,
      isActive: form.is_active
    };
    console.log('[EnrollmentPlatformAdmin] payload keys:', Object.keys(payload));
    // Get user session for Authorization
    const {
      data: sessionData
    } = await supabase.auth.getSession();
    const accessToken = sessionData?.session?.access_token;
    console.log('[EnrollmentPlatformAdmin] Session before submit:', {
      hasSession: !!sessionData?.session,
      accessToken: accessToken ? 'present' : 'MISSING',
      userId: sessionData?.session?.user?.id ?? null
    });
    if (!accessToken) {
      setError('No hay sesión activa. Inicia sesión primero en /admin/login');
      setIsSubmitting(false);
      return;
    }
    let data: any = null;
    try {
      const res = await fetch(`${SUPABASE_URL}/functions/v1/create-platform-admin`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${accessToken}`,
          apikey: SUPABASE_ANON_KEY
        },
        body: JSON.stringify(payload)
      });
      data = await res.json();
      console.log('response:', {
        status: res.status,
        data
      });
      if (!res.ok) {
        console.error('backend error:', data);
        const raw = (data?.error || data?.details || data?.message || '').toLowerCase();
        let friendlyMsg = data?.error || data?.details || data?.message || `Error del servidor (${res.status})`;
        if (raw.includes('email') || raw.includes('correo') || raw.includes('already registered') || raw.includes('already been registered')) {
          friendlyMsg = `Ya existe un usuario con el correo "${form.email}".`;
        } else if (raw.includes('national_id') || raw.includes('cédula')) {
          friendlyMsg = `Ya existe un perfil con la cédula "${form.national_id}".`;
        }
        setError(friendlyMsg);
        setIsSubmitting(false);
        return;
      }
    } catch (fetchErr: any) {
      console.error('fetch error:', fetchErr);
      setError(fetchErr.message || 'Error de conexión con el servidor.');
      setIsSubmitting(false);
      return;
    }
    setIsSubmitting(false);
    if (!data?.success) {
      setError(data?.error || data?.details || 'Error al crear el admin.');
      return;
    }
    setResult({
      user_id: data.admin_user?.user_id,
      email: data.admin_user?.email || form.email,
      full_name: data.admin_user?.full_name || form.full_name,
      role: data.admin_user?.role || form.role,
      is_active: data.admin_user?.is_active ?? form.is_active
    });
  };
  const getRoleLabel = (value: string) => ROLES.find(r => r.value === value)?.label || value;
  if (result) {
    return <div className="min-h-screen bg-[#0B0B0B] text-white font-sans flex flex-col items-center justify-center p-6" data-id="element-3077">
        <div className="max-w-md w-full space-y-6 text-center" data-id="element-3078">
          <div className="w-20 h-20 bg-[#006847]/20 rounded-full flex items-center justify-center mx-auto" data-id="element-3079">
            <CheckCircle2 className="w-10 h-10 text-[#00a870]" data-id="element-3080" />
          </div>
          <h1 className="text-2xl font-bold" data-id="element-3081">Admin de NOIZA creado</h1>
          <p className="text-white/50" data-id="element-3082">
            La cuenta de administrador fue creada exitosamente.
          </p>

          <div className="bg-[#141414] border border-white/10 rounded-2xl p-5 text-left space-y-3 text-sm" data-id="element-3083">
            <div className="flex justify-between" data-id="element-3084">
              <span className="text-white/50" data-id="element-3085">Nombre</span>
              <strong data-id="element-3086">{result.full_name}</strong>
            </div>
            <div className="flex justify-between" data-id="element-3087">
              <span className="text-white/50" data-id="element-3088">Email</span>
              <strong className="font-mono text-xs" data-id="element-3089">{result.email}</strong>
            </div>
            <div className="flex justify-between" data-id="element-3090">
              <span className="text-white/50" data-id="element-3091">Rol</span>
              <span className="inline-flex items-center gap-1.5 bg-[#006847]/20 text-[#00a870] text-xs font-bold px-2.5 py-0.5 rounded-lg" data-id="element-3092">
                <Shield className="w-3 h-3" data-id="element-3093" />
                {getRoleLabel(result.role || '')}
              </span>
            </div>
            <div className="flex justify-between" data-id="element-3094">
              <span className="text-white/50" data-id="element-3095">Estado</span>
              <strong className={result.is_active ? 'text-[#00a870]' : 'text-red-400'} data-id="element-3096">
                {result.is_active ? 'Activo' : 'Inactivo'}
              </strong>
            </div>
            <div className="flex justify-between" data-id="element-3097">
              <span className="text-white/50" data-id="element-3098">Contraseña temporal</span>
              <strong className="font-mono text-xs" data-id="element-3099">
                {form.temporary_password}
              </strong>
            </div>
            {result.user_id && <div className="flex justify-between" data-id="element-3100">
                <span className="text-white/50" data-id="element-3101">User ID</span>
                <strong className="font-mono text-[10px] text-white/60 truncate max-w-[200px]" data-id="element-3102">
                  {result.user_id}
                </strong>
              </div>}
          </div>

          <div className="bg-yellow-500/10 border border-yellow-500/20 rounded-xl p-4 text-left" data-id="element-3103">
            <p className="text-sm text-yellow-300" data-id="element-3104">
              ⚠️ Comparte las credenciales de forma segura. La contraseña
              temporal debe cambiarse en el primer inicio de sesión.
            </p>
          </div>

          <div className="flex gap-3 pt-4" data-id="element-3105">
            <button onClick={() => {
            setResult(null);
            setForm({
              full_name: '',
              email: '',
              temporary_password: '',
              phone: '',
              national_id: '',
              role: 'platform_admin',
              is_active: true
            });
          }} className="flex-1 py-3 bg-white/5 border border-white/10 rounded-xl font-bold text-white/70 hover:bg-white/10 transition-colors" data-id="element-3106">
              Crear otro admin
            </button>
            <button onClick={() => navigate('/admin/users')} className="flex-1 py-3 bg-[#006847] rounded-xl font-bold text-white hover:bg-[#005a3d] transition-colors" data-id="element-3107">
              Ver usuarios
            </button>
          </div>
        </div>
      </div>;
  }
  return <div className="min-h-screen bg-[#0B0B0B] text-white font-sans" data-id="element-3108">
      <div className="max-w-lg mx-auto px-4 sm:px-6 py-8 space-y-6" data-id="element-3109">
        {/* Header */}
        <div className="flex items-center gap-4" data-id="element-3110">
          <button onClick={() => navigate('/admin/enrollment')} className="w-10 h-10 bg-[#141414] border border-white/10 rounded-xl flex items-center justify-center hover:bg-white/5 transition-colors" data-id="element-3111">
            <ArrowLeft className="w-5 h-5" data-id="element-3112" />
          </button>
          <div data-id="element-3113">
            <h1 className="text-2xl font-bold" data-id="element-3114">Admin de NOIZA</h1>
            <p className="text-white/40 text-sm" data-id="element-3115">
              Crea usuarios internos de NOIZA con permisos de plataforma.
            </p>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6" data-id="element-3116">
          <div className="space-y-4" data-id="element-3117">
            <div className="flex items-center gap-2 pb-1 border-b border-white/5" data-id="element-3118">
              <Shield className="w-4 h-4 text-[#00a870]" data-id="element-3119" />
              <h2 className="text-sm font-bold text-white/60 uppercase tracking-widest" data-id="element-3120">
                Datos del admin
              </h2>
            </div>

            {/* Name */}
            <div data-id="element-3121">
              <label className={labelClass} data-id="element-3122">Nombre completo</label>
              <div className="relative" data-id="element-3123">
                <User className={iconClass} data-id="element-3124" />
                <input type="text" value={form.full_name} onChange={e => updateField('full_name', e.target.value)} placeholder="Juan Pérez" className={inputClass} required data-id="element-3125" />
              </div>
            </div>

            {/* Email */}
            <div data-id="element-3126">
              <label className={labelClass} data-id="element-3127">Correo electrónico</label>
              <div className="relative" data-id="element-3128">
                <Mail className={iconClass} data-id="element-3129" />
                <input type="email" value={form.email} onChange={e => updateField('email', e.target.value)} placeholder="admin@noiza.com" className={inputClass} required data-id="element-3130" />
              </div>
            </div>

            {/* Password */}
            <div data-id="element-3131">
              <label className={labelClass} data-id="element-3132">Contraseña temporal</label>
              <div className="relative" data-id="element-3133">
                <Lock className={iconClass} data-id="element-3134" />
                <input type="text" value={form.temporary_password} onChange={e => updateField('temporary_password', e.target.value)} placeholder="Mínimo 8 caracteres" className={`${inputClass} font-mono`} required minLength={8} data-id="element-3135" />
              </div>
            </div>

            {/* Phone */}
            <div data-id="element-3136">
              <label className={labelClass} data-id="element-3137">
                Teléfono{' '}
                <span className="text-white/20 normal-case" data-id="element-3138">(opcional)</span>
              </label>
              <div className="relative" data-id="element-3139">
                <Phone className={iconClass} data-id="element-3140" />
                <input type="tel" value={form.phone} onChange={e => updateField('phone', e.target.value)} placeholder="88888888" className={inputClass} data-id="element-3141" />
              </div>
            </div>

            {/* National ID */}
            <div data-id="element-3142">
              <label className={labelClass} data-id="element-3143">
                Cédula{' '}
                <span className="text-white/20 normal-case" data-id="element-3144">(opcional)</span>
              </label>
              <div className="relative" data-id="element-3145">
                <IdCard className={iconClass} data-id="element-3146" />
                <input type="text" value={form.national_id} onChange={e => updateField('national_id', e.target.value)} placeholder="Ej: 304590658" className={inputClass} data-id="element-3147" />
              </div>
            </div>

            {/* Role */}
            <div data-id="element-3148">
              <label className={labelClass} data-id="element-3149">Rol de plataforma</label>
              <div className="relative" data-id="element-3150">
                <Shield className={iconClass} data-id="element-3151" />
                <select value={form.role} onChange={e => updateField('role', e.target.value)} className={`${inputClass} appearance-none cursor-pointer`} required data-id="element-3152">
                  {ROLES.map(r => <option key={r.value} value={r.value} className="bg-[#141414] text-white" data-id="element-3153">
                      {r.label}
                    </option>)}
                </select>
              </div>
              <div className="mt-2 bg-white/5 border border-white/5 rounded-lg p-3 space-y-1" data-id="element-3154">
                {ROLES.map(r => <div key={r.value} className="flex items-center gap-2 text-xs" data-id="element-3155">
                    <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${form.role === r.value ? 'bg-[#00a870]' : 'bg-white/20'}`} data-id="element-3156" />
                    <span className={form.role === r.value ? 'text-white font-bold' : 'text-white/40'} data-id="element-3157">
                      {r.label}:
                    </span>
                    <span className="text-white/30" data-id="element-3158">{r.desc}</span>
                  </div>)}
              </div>
            </div>

            {/* Active Toggle */}
            <div className="flex items-center justify-between bg-[#141414] border border-white/10 rounded-xl p-4" data-id="element-3159">
              <span className="text-sm font-medium text-white/70" data-id="element-3160">
                Usuario activo
              </span>
              <button type="button" onClick={() => updateField('is_active', !form.is_active)} className={`w-14 h-8 rounded-full transition-colors relative ${form.is_active ? 'bg-[#006847]' : 'bg-white/10'}`} data-id="element-3161">
                <div className={`w-6 h-6 bg-white rounded-full absolute top-1 transition-transform ${form.is_active ? 'translate-x-7' : 'translate-x-1'}`} data-id="element-3162" />
              </button>
            </div>
          </div>

          {/* Error */}
          {error && <div className="p-4 bg-red-500/10 border border-red-500/20 rounded-xl flex items-start gap-3 text-red-400" data-id="element-3163">
              <AlertCircle className="w-5 h-5 shrink-0 mt-0.5" data-id="element-3164" />
              <div className="text-sm whitespace-pre-line" data-id="element-3165">{error}</div>
            </div>}

          {/* Submit */}
          <button type="submit" disabled={isSubmitting} className="w-full py-5 bg-[#006847] hover:bg-[#005a3d] disabled:bg-white/5 disabled:text-white/30 text-white font-black text-lg rounded-xl transition-all flex items-center justify-center gap-2 active:scale-95" data-id="element-3166">
            {isSubmitting ? <>
                <Loader2 className="w-6 h-6 animate-spin" data-id="element-3167" />
                Creando admin...
              </> : <>
                <Shield className="w-5 h-5" data-id="element-3168" />
                Crear Admin de NOIZA
              </>}
          </button>
        </form>
      </div>
    </div>;
}